import type { Film, Star, Topic } from "@/types/content";
import { films } from "@/mocks/films";
import { stars } from "@/mocks/stars";
import { topics } from "@/mocks/topics";

export function getFilmById(id: string): Film | undefined {
  return films.find((item) => item.id === id);
}

export function getStarById(id: string): Star | undefined {
  return stars.find((item) => item.id === id);
}

export function getTopicById(id: string): Topic | undefined {
  return topics.find((item) => item.id === id);
}

export function getStarsByIds(ids: string[]): Star[] {
  return ids
    .map((id) => getStarById(id))
    .filter((item): item is Star => Boolean(item));
}

export function getFilmsByIds(ids: string[]): Film[] {
  return ids
    .map((id) => getFilmById(id))
    .filter((item): item is Film => Boolean(item));
}

export function getTopicsByIds(ids: string[]): Topic[] {
  return ids
    .map((id) => getTopicById(id))
    .filter((item): item is Topic => Boolean(item));
}

export function getStarFilms(star: Star): Film[] {
  return getFilmsByIds(star.filmIds);
}

export function getFilmCast(film: Film): Star[] {
  return getStarsByIds(film.castIds);
}

export function getRelatedFilms(film: Film, limit = 3): Film[] {
  const sameGenre = films.filter(
    (item) =>
      item.id !== film.id &&
      (item.genres.some((g) => film.genres.includes(g)) ||
        item.director === film.director),
  );
  return sameGenre.slice(0, limit);
}

export function getRelatedTopics(topic: Topic, limit = 3): Topic[] {
  const sameTag = topics.filter(
    (item) => item.id !== topic.id && item.tag === topic.tag,
  );
  const rest = topics.filter(
    (item) => item.id !== topic.id && item.tag !== topic.tag,
  );
  return [...sameTag, ...rest].slice(0, limit);
}

export interface SearchResult {
  films: Film[];
  stars: Star[];
  topics: Topic[];
}

export function searchContent(keyword: string): SearchResult {
  const q = keyword.trim().toLowerCase();
  if (!q) {
    return { films: [], stars: [], topics: [] };
  }
  const filmHits = films.filter((item) =>
    [item.title, item.director, item.summary, ...item.genres, ...item.tags]
      .join(" ")
      .toLowerCase()
      .includes(q),
  );
  const starHits = stars.filter((item) =>
    [item.name, item.nationality, item.profession, ...item.tags]
      .join(" ")
      .toLowerCase()
      .includes(q),
  );
  const topicHits = topics.filter((item) =>
    [item.title, item.tag, item.excerpt].join(" ").toLowerCase().includes(q),
  );
  return { films: filmHits, stars: starHits, topics: topicHits };
}